import { useEffect, useMemo, useRef, useState } from 'react';
import type { RadioNowPlayingSnapshot } from '@/hooks/useRadioNowPlaying';

const DEFAULT_SWITCH_DELAY_MS = 7000;

const getTrackKey = (item: RadioNowPlayingSnapshot | null) => {
  if (!item) return '';
  return `${item.artist}__${item.title}`.trim();
};

export function useSmoothedNowPlaying(
  incoming: RadioNowPlayingSnapshot | null,
  audioIsActuallyPlaying: boolean,
  switchDelayMs: number = DEFAULT_SWITCH_DELAY_MS,
) {
  const [displayed, setDisplayed] = useState<RadioNowPlayingSnapshot | null>(incoming);
  const pendingTimerRef = useRef<number | null>(null);
  const pendingKeyRef = useRef<string>('');

  const incomingKey = useMemo(() => getTrackKey(incoming), [incoming]);
  const displayedKey = useMemo(() => getTrackKey(displayed), [displayed]);

  useEffect(() => {
    return () => {
      if (pendingTimerRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(pendingTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!incoming) return;

    // First payload should render quickly.
    if (!displayed) {
      setDisplayed(incoming);
      return;
    }

    // Same track: update auxiliary fields immediately (listeners/live/art changes).
    if (incomingKey === displayedKey) {
      setDisplayed(incoming);
      if (pendingTimerRef.current !== null && typeof window !== 'undefined') {
        window.clearTimeout(pendingTimerRef.current);
        pendingTimerRef.current = null;
      }
      pendingKeyRef.current = '';
      return;
    }

    // Different track: only switch label when audio is confirmed as actually playing.
    // This avoids showing the next title while the buffered tail of the previous song still sounds.
    if (!audioIsActuallyPlaying) {
      return;
    }

    if (pendingKeyRef.current === incomingKey) {
      return;
    }

    if (pendingTimerRef.current !== null && typeof window !== 'undefined') {
      window.clearTimeout(pendingTimerRef.current);
      pendingTimerRef.current = null;
    }

    pendingKeyRef.current = incomingKey;

    if (typeof window === 'undefined') {
      setDisplayed(incoming);
      pendingKeyRef.current = '';
      return;
    }

    pendingTimerRef.current = window.setTimeout(() => {
      setDisplayed(incoming);
      pendingKeyRef.current = '';
      pendingTimerRef.current = null;
    }, switchDelayMs);
  }, [incoming, incomingKey, displayed, displayedKey, audioIsActuallyPlaying, switchDelayMs]);

  return displayed;
}
